// ../utils.ts
var IS_BROWSER = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.navigator !== "undefined";
var IS_ANDROID = IS_BROWSER && /android/i.test(navigator.userAgent);
var SAFE_LABELLEDBY = `${IS_ANDROID ? "data" : "aria"}-labelledby`;
var DISPLAY_BLOCK = ":host(:not([hidden])) { display: block }";
var UHTMLElement = typeof HTMLElement === "undefined" ? class {
} : HTMLElement;
var events = (action, element, rest) => rest[0].split(",").forEach((type) => {
  rest[0] = type;
  Element.prototype[`${action}EventListener`].apply(element, rest);
});
var on = (element, ...rest) => events("add", element, rest);
var off = (element, ...rest) => events("remove", element, rest);
var attachStyle = (element, css) => element.attachShadow({ mode: "closed" }).append(
  createElement("slot"),
  // Unnamed slot does automatically render all top element nodes
  createElement("style", { textContent: css })
);
var asButton = (event) => {
  const isClick = "key" in event && (event.key === " " || event.key === "Enter");
  if (isClick) event.preventDefault();
  if (isClick && event.target instanceof HTMLElement) event.target.click();
  return isClick;
};
var getRoot = (node) => node.getRootNode();
var id = 0;
var useId = (el) => el ? el.id || (el.id = `:${el.nodeName.toLowerCase()}${(++id).toString(32)}`) : "";
var createElement = (tagName, props) => Object.assign(document.createElement(tagName), props);
var customElements = {
  define: (name, instance) => !IS_BROWSER || window.customElements.get(name) || window.customElements.define(name, instance)
};

// u-tabs.ts
var ARIA_CONTROLS = "aria-controls";
var UHTMLTabsElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(this, DISPLAY_BLOCK);
  }
  get tabList() {
    return queryWithoutNested("u-tablist", this)[0] || null;
  }
  get selectedIndex() {
    return [...this.tabs].findIndex((tab) => tab.ariaSelected === "true");
  }
  set selectedIndex(index) {
    if (this.tabs[index]) this.tabs[index].ariaSelected = "true";
  }
  get tabs() {
    return queryWithoutNested("u-tab", this);
  }
  get panels() {
    return queryWithoutNested("u-tabpanel", this);
  }
};
var UHTMLTabListElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(this, DISPLAY_BLOCK);
  }
  connectedCallback() {
    this.role = "tablist";
    on(this, "click,keydown", this);
  }
  disconnectedCallback() {
    off(this, "click,keydown", this);
  }
  handleEvent(event) {
    const { key } = event;
    const tabs = [...this.getElementsByTagName("u-tab")];
    let index = tabs.findIndex((tab) => tab.contains(event.target));
    if (event.defaultPrevented || index === -1) return;
    if (event.type === "click") tabs[index].selected = true;
    if (event.type === "keydown" && !asButton(event)) {
      if (key === "ArrowDown" || key === "ArrowRight")
        index = ++index % tabs.length;
      else if (key === "ArrowUp" || key === "ArrowLeft")
        index = (index || tabs.length) - 1;
      else if (key === "End") index = tabs.length - 1;
      else if (key === "Home") index = 0;
      else return;
      event.preventDefault();
      tabs[index].focus();
    }
  }
  get tabsElement() {
    return this.closest("u-tabs");
  }
};
var SKIP_ATTR_CHANGE = false;
var UHTMLTabElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(
      this,
      `:host(:not([hidden])) { cursor: pointer; display: inline-block }`
    );
  }
  connectedCallback() {
    this.selected = !!this.selected;
  }
  attributeChangedCallback(_name, prev, next) {
    if (!SKIP_ATTR_CHANGE && prev !== next && (SKIP_ATTR_CHANGE = true)) {
      const { tabs = [], panels = [], selectedIndex } = this.tabsElement || {};
      const selected = this.selected ? this : tabs[selectedIndex || 0] || this;
      let selectedPanel;
      panels.forEach((panel) => {
        panel.removeAttribute(SAFE_LABELLEDBY);
        panel.hidden = true;
      });
      tabs.forEach((tab, index) => {
        const panel = getPanel(tab) || panels[index] || null;
        if (selected === tab && panel) selectedPanel = panel;
        tab.role = "tab";
        tab.tabIndex = selected === tab ? 0 : -1;
        tab.ariaSelected = `${selected === tab}`;
        tab.setAttribute(ARIA_CONTROLS, useId(panel));
        panel?.toggleAttribute("hidden", selectedPanel !== panel);
        panel?.setAttribute(
          SAFE_LABELLEDBY,
          useId(selectedPanel === panel ? selected : tab)
        );
      });
      SKIP_ATTR_CHANGE = false;
    }
  }
  get tabsElement() {
    return this.closest("u-tabs");
  }
  get tabList() {
    return this.closest("u-tablist");
  }
  get selected() {
    return this.ariaSelected === "true";
  }
  set selected(value) {
    this.ariaSelected = `${value}`;
  }
  /** Retrieves the ordinal position of an tab in a tablist. */
  get index() {
    return Array.from(this.tabsElement?.tabs || []).indexOf(this);
  }
  get panel() {
    return getPanel(this);
  }
};
UHTMLTabElement.observedAttributes = ["id", "aria-selected", ARIA_CONTROLS];
var UHTMLTabPanelElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(this, DISPLAY_BLOCK);
  }
  connectedCallback() {
    this.role = "tabpanel";
    this.hidden = Array.from(this.tabs).every((tab) => !tab.selected);
  }
  attributeChangedCallback(_name, prev, next) {
    if (SKIP_ATTR_CHANGE || prev === next) return;
    getTabs(this, prev).forEach((tab) => tab.setAttribute(ARIA_CONTROLS, next));
  }
  get tabsElement() {
    return this.closest("u-tabs");
  }
  get tabs() {
    return getTabs(this, this.id);
  }
};
UHTMLTabPanelElement.observedAttributes = ["id"];
var queryWithoutNested = (tag, self) => {
  const selector = `${tag}:not(:scope ${self.nodeName}:not(:scope) ${tag})`;
  return self.querySelectorAll(selector);
};
var getPanel = (self) => {
  const css = `u-tabpanel[id="${self.getAttribute(ARIA_CONTROLS)}"]`;
  return getRoot(self).querySelector(css) || document.querySelector(css);
};
var getTabs = (self, id2) => {
  const css = `u-tab[${ARIA_CONTROLS}="${id2}"]`;
  return getRoot(self).querySelectorAll(css);
};
customElements.define("u-tabs", UHTMLTabsElement);
customElements.define("u-tablist", UHTMLTabListElement);
customElements.define("u-tab", UHTMLTabElement);
customElements.define("u-tabpanel", UHTMLTabPanelElement);

export { UHTMLTabElement, UHTMLTabListElement, UHTMLTabPanelElement, UHTMLTabsElement };
